/**
 * One function per API call. Pages and hooks import from here rather than
 * building paths inline, so the endpoint list is auditable in one place.
 */

import type {
  AppConfig,
  Category,
  DiscountReason,
  PaymentTerminal,
  Product,
  ProvisioningApproveBody,
  ProvisioningApproveResponse,
  ProvisioningListResponse,
  ProvisioningRejectResponse,
  SalesReport,
  ShiftReportRow,
} from '@equinox/shared';
import { api, queryString } from '../lib/apiClient.js';
import { endOfDayIso, startOfDayIso } from '../lib/reportDates.js';
import type {
  AdminStaff,
  AdminTerminal,
  CreateCategoryRequest,
  CreateDiscountReasonRequest,
  CreatePaymentTerminalRequest,
  CreateProductRequest,
  CreateStaffRequest,
  ImportResponse,
  LoginRequest,
  MeResponse,
  RegisterTerminalRequest,
  SalesReportFilter,
  SetCeilingRequest,
  SetPinRequest,
  SetPasswordRequest,
  SyncRejectionRow,
  TerminalWithToken,
  UpdateCategoryRequest,
  UpdateDiscountReasonRequest,
  UpdatePaymentTerminalRequest,
  UpdateProductRequest,
  UpdateStaffRequest,
} from './types.js';

// ---- Auth ----
//
// The server registers admin auth under /auth/admin/*, not /auth/* — see
// packages/server/src/routes/auth.ts. Getting this wrong is exactly how the
// 2026-07-31 acceptance round found nobody could log in at all (finding F1).

export const authApi = {
  login: (body: LoginRequest) => api.post<void>('/auth/admin/login', body),
  logout: () => api.post<void>('/auth/admin/logout'),
  me: () => api.get<MeResponse>('/auth/admin/session'),
};

// ---- Staff ----

export const staffApi = {
  list: () => api.get<AdminStaff[]>('/staff'),
  create: (body: CreateStaffRequest) => api.post<AdminStaff>('/staff', body),
  update: (id: string, body: UpdateStaffRequest) => api.patch<AdminStaff>(`/staff/${id}`, body),
  setPin: (id: string, body: SetPinRequest) => api.post<void>(`/staff/${id}/pin`, body),
  setPassword: (id: string, body: SetPasswordRequest) => api.post<void>(`/staff/${id}/password`, body),
  setCeiling: (id: string, body: SetCeilingRequest) => api.patch<AdminStaff>(`/staff/${id}`, body),
  deactivate: (id: string) => api.patch<AdminStaff>(`/staff/${id}`, { active: false }),
  activate: (id: string) => api.patch<AdminStaff>(`/staff/${id}`, { active: true }),
};

// ---- Terminals ----
//
// The server registers POST /terminals/:id/rotate-token and
// POST /terminals/:id/revoke — never a /token sub-path, and never a DELETE
// (see packages/server/src/routes/terminals.ts). A previous version of this
// client called POST/DELETE .../token, which the server has no route for at
// all, so both calls 404'd with "route not found" (bug 4). Registering a
// terminal also mints and returns a token the same way rotating one does —
// `register` and `rotateToken` share the `TerminalWithToken` response shape
// for that reason. See docs/decisions/2026-07-31-terminal-token-endpoints-fix.md.

export const terminalsApi = {
  // includeInactive on purpose (acceptance R3-F2): a revoked terminal sets
  // active=false, and a list that hides it makes "Reactivar" unreachable —
  // the terminal simply vanished on the next load, unrecoverable from the UI.
  list: () => api.get<AdminTerminal[]>('/terminals?includeInactive=true'),
  register: (body: RegisterTerminalRequest) => api.post<TerminalWithToken>('/terminals', body),
  rotateToken: (id: string) => api.post<TerminalWithToken>(`/terminals/${id}/rotate-token`),
  revoke: (id: string) => api.post<AdminTerminal>(`/terminals/${id}/revoke`),
  deactivate: (id: string) => api.patch<AdminTerminal>(`/terminals/${id}`, { active: false }),
  activate: (id: string) => api.patch<AdminTerminal>(`/terminals/${id}`, { active: true }),
  /** null clears the override so the device-reported zone rules again. */
  setTimeZoneOverride: (id: string, timeZoneOverride: string | null) =>
    api.patch<AdminTerminal>(`/terminals/${id}`, { timeZoneOverride }),
};

// ---- Categories ----

export const categoriesApi = {
  list: () => api.get<Category[]>('/categories'),
  create: (body: CreateCategoryRequest) => api.post<Category>('/categories', body),
  update: (id: string, body: UpdateCategoryRequest) => api.patch<Category>(`/categories/${id}`, body),
  deactivate: (id: string) => api.patch<Category>(`/categories/${id}`, { active: false }),
  activate: (id: string) => api.patch<Category>(`/categories/${id}`, { active: true }),
};

// ---- Products ----

export const productsApi = {
  list: () => api.get<Product[]>('/products'),
  create: (body: CreateProductRequest) => api.post<Product>('/products', body),
  update: (id: string, body: UpdateProductRequest) => api.patch<Product>(`/products/${id}`, body),
  setPrice: (id: string, priceCents: number) => api.patch<Product>(`/products/${id}`, { priceCents }),
  deactivate: (id: string) => api.patch<Product>(`/products/${id}`, { active: false }),
  activate: (id: string) => api.patch<Product>(`/products/${id}`, { active: true }),
  importCsv: (file: File | Blob, dryRun: boolean) => {
    const form = new FormData();
    form.set('file', file);
    return api.postForm<ImportResponse>(`/import/products${dryRun ? '?dryRun=1' : ''}`, form);
  },
};

// ---- Discount reasons ----

export const discountReasonsApi = {
  list: () => api.get<DiscountReason[]>('/discount-reasons'),
  create: (body: CreateDiscountReasonRequest) => api.post<DiscountReason>('/discount-reasons', body),
  update: (id: string, body: UpdateDiscountReasonRequest) =>
    api.patch<DiscountReason>(`/discount-reasons/${id}`, body),
  deactivate: (id: string) => api.patch<DiscountReason>(`/discount-reasons/${id}`, { active: false }),
  activate: (id: string) => api.patch<DiscountReason>(`/discount-reasons/${id}`, { active: true }),
};

// ---- Payment terminals (Mercado Pago / Clip push) ----
//
// Deliberately named "payment-terminals", distinct from the "/terminals"
// endpoint above — that one is the POS device (Sunmi tablet), this one is a
// registered card-collection device. See
// docs/decisions/2026-10-15-payment-terminal-push.md.

export const paymentTerminalsApi = {
  list: (includeInactive = false) =>
    api.get<PaymentTerminal[]>(
      `/payment-terminals${queryString({ includeInactive: includeInactive ? '1' : undefined })}`,
    ),
  create: (body: CreatePaymentTerminalRequest) => api.post<PaymentTerminal>('/payment-terminals', body),
  update: (id: string, body: UpdatePaymentTerminalRequest) =>
    api.patch<PaymentTerminal>(`/payment-terminals/${id}`, body),
  deactivate: (id: string) => api.patch<PaymentTerminal>(`/payment-terminals/${id}`, { active: false }),
  activate: (id: string) => api.patch<PaymentTerminal>(`/payment-terminals/${id}`, { active: true }),
};

// ---- Config ----

export const configApi = {
  get: () => api.get<AppConfig>('/config'),
  update: (body: Partial<AppConfig>) => api.patch<AppConfig>('/config', body),
};

// ---- Reports ----
//
// The report screens only ever collect a bare YYYY-MM-DD from an
// <input type="date"> (see lib/format.ts), but the server requires a full
// ISO datetime with an offset. `startOfDayIso`/`endOfDayIso` expand that
// date into the full local day in America/Mexico_City — not naively by
// appending "Z", which would shift the boundary by the zone's six-hour
// offset and silently misfile sales at the edges of the day (finding F10).
// A value that is already a full ISO datetime passes through unchanged.
//
// `sales` returns @equinox/shared's `SalesReport` — a flat shape
// (`grossSalesCents`, `byPaymentMethod`, `topItemsByUnits`, ...). A
// previous version of this client declared its own `ReportResponse` with a
// nested `totals` object that the server never sends, so
// `report.totals.grossCents` threw on every load and the page rendered
// blank (bug 2). `shifts` unwraps `{ shifts: [...] }` — the server's actual
// `ShiftReport` envelope — into a bare array for the page to consume; a
// previous version of this client typed the response as a bare array
// directly, so `rows.map` threw the moment a real server answered (bug 3).
// See docs/decisions/2026-07-31-reports-contract-fix.md.

export const reportsApi = {
  sales: (query: SalesReportFilter) =>
    api.get<SalesReport>(
      `/reports${queryString({
        from: startOfDayIso(query.from),
        to: endOfDayIso(query.to),
        method: query.method,
        staffId: query.staffId,
        terminalId: query.terminalId,
      })}`,
    ),
  shifts: (from: string, to: string) =>
    api
      .get<{ shifts: ShiftReportRow[] }>(
        `/reports/shifts${queryString({ from: startOfDayIso(from), to: endOfDayIso(to) })}`,
      )
      .then((r) => r.shifts),
};

// ---- Sync health ----
//
// The contract this was coded against: GET /sync/rejections ->
// { rejections: [...] }, ?includeResolved=1 to include already-resolved
// rows, and POST /sync/rejections/:id/resolve to mark one resolved. There
// is no endpoint for a per-terminal pending-envelope count because the
// server cannot see an outbox that has not reached it yet — that queue
// lives in the terminal's IndexedDB until it syncs (spec section 5). "Stuck
// terminal" is therefore inferred client-side from
// terminals.lastSeenAt/lastSyncAt staleness plus unresolved rows here, not
// from a pending count. See docs/decisions/2026-07-31-sync-health-pending-count.md.
//
// `normalizeRejections` accepts either the wrapped shape above or a bare
// array: the server route for this was still being actively written in a
// concurrent worktree while this client code was, and its own test
// (`packages/server/test/routes/sync-rejections.test.ts`) currently asserts
// a bare array, not the wrapped shape it was described as. Tolerating
// either avoids a second F1/F4-shaped contract break the moment the two
// branches merge — see docs/decisions/2026-07-31-auth-path-and-rejections-contract-fix.md.

function normalizeRejections(body: { rejections: SyncRejectionRow[] } | SyncRejectionRow[]): SyncRejectionRow[] {
  return Array.isArray(body) ? body : body.rejections;
}

export const syncApi = {
  rejections: (includeResolved = false) =>
    api
      .get<{ rejections: SyncRejectionRow[] } | SyncRejectionRow[]>(
        `/sync/rejections${queryString({ includeResolved: includeResolved ? '1' : undefined })}`,
      )
      .then(normalizeRejections),
  resolveRejection: (id: number) => api.post<void>(`/sync/rejections/${id}/resolve`),
};

// ---- stock & media ----
//
// Kept as its own export rather than grown onto `productsApi` above: this
// endpoint (`PATCH /products/:id/stock`) is deliberately separate from the
// generic product PATCH on the server too (see routes/products.ts) — it is
// the only way stock_qty ever changes, always recording a stock_movements
// row, never a blind column write through the general update body.
export const productStockApi = {
  adjust: (id: string, stockQty: number) => api.patch<Product>(`/products/${id}/stock`, { stockQty }),
  /** Same contract for one option (flavour): "set to X", always a ledger row. */
  adjustOption: (id: string, optionId: string, stockQty: number) =>
    api.patch<Product>(`/products/${id}/options/${optionId}/stock`, { stockQty }),
};

// ---- Terminal auto-provisioning ----
//
// Wire shapes come straight from @equinox/shared's provisioning.ts — the
// compile-time contract packages/server's routes/provisioning.ts was built
// against — never redeclared here. See
// docs/decisions/2026-07-31-provisioning-admin-panel.md.
export const provisioningApi = {
  list: () => api.get<ProvisioningListResponse>('/provision'),
  approve: (terminalId: string, body: ProvisioningApproveBody = {}) =>
    api.post<ProvisioningApproveResponse>(`/provision/${terminalId}/approve`, body),
  reject: (terminalId: string) => api.post<ProvisioningRejectResponse>(`/provision/${terminalId}/reject`),
};
